import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { expandAnalyticsLayerSections } from './expandAnalyticsLayerSections';

export type NestedSitrepSection = SitrepSectionType & {
  children: SitrepSectionType[];
};

/**
 * Group flat sitrep sections into top-level parents with nested children.
 * Children are ordered by `order`. Orphaned children (missing parent) surface
 * as top-level so content is never hidden.
 */
export function nestSitrepSections(
  sections: SitrepSectionType[]
): NestedSitrepSection[] {
  const ordered = [...expandAnalyticsLayerSections(sections)].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
  const byParent = new Map<string, SitrepSectionType[]>();

  for (const section of ordered) {
    const parentId = section.parent_section_id;
    if (!parentId) continue;
    const list = byParent.get(parentId) ?? [];
    list.push(section);
    byParent.set(parentId, list);
  }

  const topLevel: NestedSitrepSection[] = [];
  const childIds = new Set<string>();

  for (const section of ordered) {
    if (section.parent_section_id) {
      childIds.add(section.id);
    }
  }

  for (const section of ordered) {
    if (childIds.has(section.id)) continue;
    topLevel.push({
      ...section,
      children: byParent.get(section.id) ?? [],
    });
  }

  // Orphans whose parent is missing from this list.
  for (const section of ordered) {
    if (!section.parent_section_id) continue;
    const parentExists = ordered.some(
      (s) => s.id === section.parent_section_id
    );
    if (!parentExists && !topLevel.some((s) => s.id === section.id)) {
      topLevel.push({ ...section, children: [] });
    }
  }

  return topLevel;
}

/** AI sections under a category, including sections nested further down. */
export function aiEnabledSectionIdsUnder(
  sections: SitrepSectionType[],
  rootId: string
): string[] {
  const childrenByParent = new Map<string, SitrepSectionType[]>();
  for (const section of sections) {
    const parentId = section.parent_section_id;
    if (!parentId) continue;
    const list = childrenByParent.get(parentId) ?? [];
    list.push(section);
    childrenByParent.set(parentId, list);
  }
  const ids: string[] = [];
  const walk = (id: string) => {
    for (const child of childrenByParent.get(id) ?? []) {
      const nested = childrenByParent.get(child.id) ?? [];
      if (child.type === 'ai' && nested.length === 0) ids.push(child.id);
      walk(child.id);
    }
  };
  walk(rootId);
  return ids;
}

/** Depth-first ids matching the document outline: each root, then its children. */
export function flattenNestedSectionIds(
  nested: NestedSitrepSection[]
): string[] {
  const ids: string[] = [];
  for (const root of nested) {
    ids.push(root.id);
    for (const child of root.children) ids.push(child.id);
  }
  return ids;
}

/**
 * Map an outline drop onto a 0-based `targetOrder` for moveSection. Roots
 * (categories/uncategorized sections) reorder among other roots, treating a
 * drop on a nested row as a drop on that row's category. Subsections reorder
 * only among siblings.
 */
export function resolveOutlineReorderTarget(params: {
  nested: NestedSitrepSection[];
  activeId: string;
  overId: string;
}): { sectionId: string; targetOrder: number } | null {
  const { nested, activeId, overId } = params;
  if (activeId === overId) return null;

  const flatIds = flattenNestedSectionIds(nested);
  const childrenByParent = new Map<string, string[]>();
  const parentById = new Map<string, string | null>();

  for (const root of nested) {
    parentById.set(root.id, null);
    childrenByParent.set(
      root.id,
      root.children.map((child) => child.id)
    );
    for (const child of root.children) {
      parentById.set(child.id, root.id);
    }
  }

  if (!parentById.has(activeId) || !parentById.has(overId)) return null;

  const activeParent = parentById.get(activeId) ?? null;
  const overParent = parentById.get(overId) ?? null;
  const activeIndex = flatIds.indexOf(activeId);
  const overIndex = flatIds.indexOf(overId);
  if (activeIndex < 0 || overIndex < 0) return null;

  if (activeParent === null) {
    const overRootId = overParent ?? overId;
    if (overRootId === activeId) return null;
    const overRootIndex = flatIds.indexOf(overRootId);
    const overChildIds = childrenByParent.get(overRootId) ?? [];
    const overBlockEnd = overChildIds.length
      ? flatIds.indexOf(overChildIds[overChildIds.length - 1])
      : overRootIndex;
    const targetOrder =
      activeIndex < overRootIndex ? overBlockEnd : overRootIndex;
    return { sectionId: activeId, targetOrder };
  }

  if (overParent === activeParent) {
    return { sectionId: activeId, targetOrder: overIndex };
  }

  return null;
}
